import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../config/api";
import CreateTaskModal from "./CreateTaskModal";
import BulkTaskUploadModal from "./BulkTaskUploadModal";
import Toast, { useToast } from "./Toast";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { FIELD_KEY, STATUS_LABELS, COLS, colStyle } from "./sheetConstants";
import PhaseRow from "./PhaseRow";
import TaskRow from "./TaskRow";

// ── Small layout-only sub-components ─────────────────────────────────────────

function HeaderRow() {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 12px", height: 32, background: "#f8fafc", borderBottom: "2px solid var(--border-strong)" }}>
      {COLS.map(col => (
        <div key={col.id} style={{ ...colStyle(col), display: "flex", alignItems: "center", fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", overflow: "hidden", whiteSpace: "nowrap", paddingLeft: col.id === "task" ? 6 : 0 }}>
          {col.label}
        </div>
      ))}
    </div>
  );
}

function AddPhaseRow({ onAdd }) {
  const [active, setActive] = useState(false);
  const [name, setName] = useState("");
  const inputRef = useRef(null);

  function activate() { setActive(true); setTimeout(() => inputRef.current?.focus(), 50); }
  function submit() {
    if (!name.trim()) { setActive(false); return; }
    onAdd(name.trim()); setName(""); setActive(false);
  }

  if (!active) {
    return (
      <div
        style={{ display: "flex", alignItems: "center", padding: "0 16px", height: 38, cursor: "pointer", color: "#94a3b8", fontSize: 12, gap: 6, borderTop: "1px solid var(--border)", transition: "background 0.1s" }}
        onMouseEnter={e => e.currentTarget.style.background = "var(--surface-2)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        onClick={activate}
      >
        <span style={{ fontSize: 15, fontWeight: 300, lineHeight: 1, color: "#6366f1" }}>+</span>
        <span style={{ color: "#6366f1", fontWeight: 600 }}>Add Phase</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 14px", height: 44, gap: 8, borderTop: "1px solid var(--border)", background: "#f5f3ff" }}>
      <input
        ref={inputRef} value={name} onChange={e => setName(e.target.value)} placeholder="Phase name..."
        onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") setActive(false); }}
        style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: "6px 10px", borderRadius: 7, border: "1.5px solid #a5b4fc", outline: "none", background: "white", color: "#3730a3" }}
      />
      <button onClick={submit} style={{ padding: "6px 14px", borderRadius: 7, background: "#6366f1", color: "white", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Add</button>
      <button onClick={() => setActive(false)} style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid var(--border-strong)", background: "transparent", cursor: "pointer", fontSize: 12, color: "var(--text-2)" }}>Cancel</button>
    </div>
  );
}

function DropZone() {
  return (
    <div style={{ height: 34, margin: "6px 16px", borderRadius: 7, border: "2px dashed #c7d2fe", background: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
      <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 500 }}>Drop task here</span>
    </div>
  );
}

function UnphasedHeader({ count }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 12px", height: 36, borderBottom: "1px solid var(--border)", borderLeft: "3px solid #94a3b8", background: "var(--surface-2, #f8fafc)", gap: 8 }}>
      <span style={{ fontWeight: 700, fontSize: 12.5, color: "var(--text-3)", flex: 1 }}>Unphased</span>
      <span style={{ fontSize: 11, color: "var(--text-3)", background: "var(--border-strong)", padding: "1px 7px", borderRadius: 10 }}>{count}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProjectSheetView({ projectId, tasks: initialTasks, canManage, canBulkUpload, onRefresh, phases: initialPhases, phaseError: initialPhaseError, totalTaskCount }) {
  const navigate = useNavigate();
  const [phases, setPhases] = useState(initialPhases || []);
  const [tasks, setTasks] = useState(initialTasks);
  const [phaseError, setPhaseError] = useState(initialPhaseError || "");
  const [collapsed, setCollapsed] = useState({});
  const [addTaskPhaseId, setAddTaskPhaseId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);
  const { toast, showToast } = useToast(2000);
  const [filters, setFilters] = useState({ assignee: "", status: "" });
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => { setTasks(initialTasks); }, [initialTasks]);
  useEffect(() => { setPhases(initialPhases || []); }, [initialPhases]);
  useEffect(() => { setPhaseError(initialPhaseError || ""); }, [initialPhaseError]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    setLoading(false);
    apiFetch("/api/users/assignable")
      .then(r => r.ok ? r.json() : [])
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [projectId]); // eslint-disable-line

  async function loadPhases() {
    try {
      const res = await apiFetch(`/api/projects/${projectId}/phases`);
      if (res.ok) {
        setPhases(await res.json());
        setPhaseError("");
      } else {
        setPhaseError("Could not load phases. Tasks will appear as unphased.");
      }
    } catch (_) {
      setPhaseError("Could not load phases. Tasks will appear as unphased.");
    }
  }

  async function addPhase(name) {
    const res = await apiFetch(`/api/projects/${projectId}/phases`, { method: "POST", body: JSON.stringify({ name }) });
    if (res.ok) loadPhases();
  }

  async function renamePhase(phaseId, newName) {
    const phase = phases.find(p => p.id === phaseId);
    if (!phase) return;
    const res = await apiFetch(`/api/projects/${projectId}/phases/${phaseId}`, {
      method: "PUT",
      body: JSON.stringify({ name: newName, isCollapsed: phase.isCollapsed ?? false }),
    });
    if (res.ok) {
      setPhases(prev => prev.map(p => p.id === phaseId ? { ...p, name: newName } : p));
      showToast("Phase renamed");
    }
  }

  async function deletePhase(phaseId) {
    const res = await apiFetch(`/api/projects/${projectId}/phases/${phaseId}`, { method: "DELETE" });
    if (res.ok || res.status === 204) {
      setPhases(prev => prev.filter(p => p.id !== phaseId));
      setTasks(prev => prev.map(t => t.phaseId === phaseId ? { ...t, phaseId: null } : t));
      showToast("Phase deleted");
    }
  }

  function toggleCollapse(phaseId) {
    const phase = phases.find(p => p.id === phaseId);
    const currentVal = collapsed[phaseId] ?? phase?.isCollapsed ?? false;
    const newVal = !currentVal;
    setCollapsed(c => ({ ...c, [phaseId]: newVal }));
    if (phase) {
      apiFetch(`/api/projects/${projectId}/phases/${phaseId}`, {
        method: "PUT",
        body: JSON.stringify({ name: phase.name, isCollapsed: newVal }),
      });
    }
  }

  const saveField = useCallback(async (taskId, field, value, currentTask) => {
    if (field === "status") {
      const res = await apiFetch(`/api/tasks/${taskId}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: value }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Invalid status transition");
      }
      onRefresh();
      return;
    }

    const body = {
      title: currentTask.title, projectId: currentTask.projectId || null,
      assignedToUserId: currentTask.assignedToUserId,
      dueDate: currentTask.dueDate ? currentTask.dueDate.split("T")[0] : null,
      status: currentTask.status, priority: currentTask.priority || "Medium",
      brief: currentTask.brief || null,
      expectedOutcome: currentTask.expectedOutcome || null,
      phaseId: currentTask.phaseId || null,
      startDate: currentTask.startDate ? currentTask.startDate.split("T")[0] : null,
      resourceAllocationPct: currentTask.resourceAllocationPct ?? null,
      atRisk: currentTask.atRisk,
      [FIELD_KEY[field] || field]: value,
    };
    const res = await apiFetch(`/api/tasks/${taskId}`, { method: "PUT", body: JSON.stringify(body) });
    if (!res.ok) throw new Error("Save failed");
    const key = FIELD_KEY[field] || field;
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const updated = { ...t, [key]: value };
      if (field === "assignee") {
        const u = users.find(u => u.id === value);
        updated.assignedToUserName = u?.name || t.assignedToUserName;
      }
      return updated;
    }));
  }, [onRefresh, users]);

  function handleDragStart(event) {
    setActiveTask(tasks.find(t => t.id === event.active.id) || null);
  }

  function handleDragEnd(event) {
    setActiveTask(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    function getGroupId(taskId) {
      const t = tasks.find(t => t.id === taskId);
      return t?.phaseId || "__unphased__";
    }

    const activeGroup = getGroupId(active.id);
    const overGroup = getGroupId(over.id);

    setTasks(prev => {
      let updated = [...prev];

      if (activeGroup === overGroup) {
        const groupTasks = updated.filter(t => (t.phaseId || "__unphased__") === activeGroup).sort((a, b) => a.order - b.order);
        const reordered = arrayMove(groupTasks, groupTasks.findIndex(t => t.id === active.id), groupTasks.findIndex(t => t.id === over.id)).map((t, i) => ({ ...t, order: i }));
        updated = updated.map(t => reordered.find(r => r.id === t.id) || t);
      } else {
        const newPhaseId = updated.find(t => t.id === over.id)?.phaseId || null;
        updated = updated.map(t => t.id === active.id ? { ...t, phaseId: newPhaseId } : t);

        const destTasks = updated.filter(t => (t.phaseId || "__unphased__") === overGroup).sort((a, b) => a.order - b.order);
        const reordered = arrayMove(destTasks, destTasks.findIndex(t => t.id === active.id), destTasks.findIndex(t => t.id === over.id)).map((t, i) => ({ ...t, order: i }));
        const srcTasks = updated.filter(t => t.id !== active.id && (t.phaseId || "__unphased__") === activeGroup).sort((a, b) => a.order - b.order).map((t, i) => ({ ...t, order: i }));

        updated = updated.map(t => reordered.find(r => r.id === t.id) || srcTasks.find(r => r.id === t.id) || t);
      }

      apiFetch("/api/tasks/reorder", { method: "PUT", body: JSON.stringify({ items: updated.map(t => ({ taskId: t.id, phaseId: t.phaseId || null, order: t.order })) }) }).catch(() => {});
      return updated;
    });
  }

  const visibleTasks = tasks.filter(t => {
    if (filters.assignee && t.assignedToUserId !== filters.assignee) return false;
    if (filters.status && t.status !== filters.status) return false;
    return true;
  });

  const hasFilter = !!(filters.assignee || filters.status);

  const assignees = [...new Map(
    tasks.filter(t => t.assignedToUserId)
      .map(t => [t.assignedToUserId, t.assignedToUserName || t.assignedToUserId])
  ).entries()].sort((a, b) => a[1].localeCompare(b[1]));

  const tasksByPhase = {};
  const unphasedTasks = [];
  for (const t of visibleTasks) {
    if (t.phaseId) {
      if (!tasksByPhase[t.phaseId]) tasksByPhase[t.phaseId] = [];
      tasksByPhase[t.phaseId].push(t);
    } else {
      unphasedTasks.push(t);
    }
  }
  for (const key of Object.keys(tasksByPhase)) tasksByPhase[key].sort((a, b) => a.order - b.order);
  unphasedTasks.sort((a, b) => a.order - b.order);

  if (loading) {
    return (
      <div style={{ borderRadius: 12, border: "1px solid var(--border-strong)", padding: "40px 24px", textAlign: "center", color: "var(--text-3)", fontSize: 13, background: "var(--surface)" }}>
        Loading sheet...
      </div>
    );
  }

  return (
    <>
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ borderRadius: "12px 12px 0 0", border: "1px solid var(--border-strong)", borderBottom: "none", background: "var(--surface)", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", flexShrink: 0 }}>Filter</span>
          <select
            value={filters.assignee}
            onChange={e => setFilters(f => ({ ...f, assignee: e.target.value }))}
            style={{ height: 30, padding: "0 10px", fontSize: 12, borderRadius: 6, border: `1px solid ${filters.assignee ? "#6366f1" : "var(--border-strong)"}`, background: filters.assignee ? "#eef2ff" : "var(--surface)", color: filters.assignee ? "#4338ca" : "var(--text-2)", outline: "none", cursor: "pointer" }}
          >
            <option value="">All Assignees</option>
            {assignees.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            style={{ height: 30, padding: "0 10px", fontSize: 12, borderRadius: 6, border: `1px solid ${filters.status ? "#6366f1" : "var(--border-strong)"}`, background: filters.status ? "#eef2ff" : "var(--surface)", color: filters.status ? "#4338ca" : "var(--text-2)", outline: "none", cursor: "pointer" }}
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {hasFilter && (
            <>
              <button
                onClick={() => setFilters({ assignee: "", status: "" })}
                style={{ height: 30, padding: "0 10px", fontSize: 12, borderRadius: 6, border: "none", background: "transparent", color: "var(--text-3)", cursor: "pointer" }}
              >
                ✕ Clear
              </button>
              <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: 2 }}>
                {visibleTasks.length} of {tasks.length}
              </span>
            </>
          )}
          <div style={{ flex: 1 }} />
          {canBulkUpload && (
            <button
              onClick={() => setShowBulkUpload(true)}
              style={{ height: 30, padding: "0 14px", fontSize: 12, fontWeight: 600, borderRadius: 6, border: "1px solid var(--border-strong)", background: "var(--surface-2)", color: "var(--text-2)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              ⬆ Bulk Upload
            </button>
          )}
        </div>
        {totalTaskCount > 500 && (
          <div style={{ padding: "6px 14px", background: "var(--warn-soft, #fffbeb)", borderTop: "1px solid var(--warn, #f59e0b)", fontSize: 12, color: "var(--warn-text, #b45309)", fontWeight: 500 }}>
            ⚠ This project has <strong>{totalTaskCount}</strong> tasks — only the first 500 are shown. Use filters to narrow the list.
          </div>
        )}
      </div>

      <div style={{ borderRadius: "0 0 12px 12px", border: "1px solid var(--border-strong)", overflow: "hidden", background: "var(--surface)", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        {phaseError && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", background: "var(--warn-soft, #fffbeb)", borderBottom: "1px solid var(--warn, #f59e0b)", fontSize: 12, color: "var(--warn-text, #b45309)", gap: 10 }}>
            <span>⚠ {phaseError}</span>
            <button
              onClick={onRefresh}
              style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 5, border: "1px solid var(--warn, #f59e0b)", background: "white", color: "var(--warn-text, #b45309)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Retry
            </button>
          </div>
        )}
        <HeaderRow />

        {phases.map(phase => {
          const phaseTasks = tasksByPhase[phase.id] || [];
          const done = phaseTasks.filter(t => t.status === "Completed").length;
          const isCollapsed = collapsed[phase.id] ?? phase.isCollapsed;

          return (
            <div key={phase.id}>
              <PhaseRow phase={phase} taskCount={phaseTasks.length} completedCount={done}
                collapsed={isCollapsed} onToggle={() => toggleCollapse(phase.id)}
                onAddTask={() => setAddTaskPhaseId(phase.id)}
                onRename={renamePhase} onDelete={deletePhase}
                canManage={canManage} />
              {!isCollapsed && (
                <SortableContext items={phaseTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  {phaseTasks.map(task => (
                    <TaskRow key={task.id} task={task} indent canManage={canManage} users={users} onSave={saveField} onSaved={() => showToast("Saved")} onNavigate={navigate} />
                  ))}
                  {phaseTasks.length === 0 && <DropZone />}
                </SortableContext>
              )}
            </div>
          );
        })}

        {unphasedTasks.length > 0 && (
          <div>
            <UnphasedHeader count={unphasedTasks.length} />
            <SortableContext items={unphasedTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
              {unphasedTasks.map(task => (
                <TaskRow key={task.id} task={task} indent={false} canManage={canManage} users={users} onSave={saveField} onSaved={() => showToast("Saved")} onNavigate={navigate} />
              ))}
            </SortableContext>
          </div>
        )}

        {phases.length === 0 && unphasedTasks.length === 0 && (
          <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-2)", marginBottom: 6 }}>
              {hasFilter ? "No tasks match your filters" : "No phases or tasks yet"}
            </div>
            <div style={{ fontSize: 12 }}>
              {hasFilter ? "Try clearing the filters above." : "Add a phase below to start organising this project."}
            </div>
          </div>
        )}

        {canManage && <AddPhaseRow onAdd={addPhase} />}

        <CreateTaskModal open={!!addTaskPhaseId} projectId={projectId} phaseId={addTaskPhaseId}
          onClose={() => setAddTaskPhaseId(null)}
          onCreated={() => { setAddTaskPhaseId(null); onRefresh(); }} />
      </div>

      <DragOverlay>
        {activeTask ? (
          <TaskRow task={activeTask} indent canManage={false} users={users} onSave={() => {}} isDragOverlay onNavigate={navigate} />
        ) : null}
      </DragOverlay>
    </DndContext>
    <Toast toast={toast} />
    <BulkTaskUploadModal
      open={showBulkUpload}
      projectId={projectId}
      onClose={() => setShowBulkUpload(false)}
      onCreated={(count) => { setShowBulkUpload(false); onRefresh(); showToast(`${count} task${count !== 1 ? "s" : ""} created`); }}
    />
    </>
  );
}
